import { BitmapText, Container } from "pixi.js";
import type { PresentationTaskRunner } from "@slot/presentation";
import { BITMAP_FONTS, setCoinText } from "../views/textStyles";
import { FX_SCOPE } from "./fxScope";
import { MULTIPLIER_LAYOUT } from "./layout";

/**
 * A multiplier's "xn", centred on its point: on its cell, and riding a MULTI fire streak's head. In the artist's golden
 * digits (the badge's Roboto if they are not in).
 */
export function multiplierLabel(text: string): BitmapText {
  const label = new BitmapText({ text: "", style: { fontFamily: BITMAP_FONTS.badge, fontSize: MULTIPLIER_LAYOUT.fontSize }, label: "Value" });
  label.anchor.set(0.5);
  setMultiplierText(label, text);
  return label;
}

function setMultiplierText(label: BitmapText, text: string): void {
  setCoinText(label, text, MULTIPLIER_LAYOUT.fontSize, BITMAP_FONTS.badge);
}

interface Multiplier {
  root: Container;
  text: BitmapText;
}

/**
 * The cell multipliers MULTI throws in the bonus: "×n" on its cell's corner, over whatever is on the cell (a multiplier
 * belongs to the cell: it waits on an empty cell for what lands there). The number a MULTI fire streak carried on its
 * head, settled there; no badge under it (the user, 2026-10-12). In the grid root's space, inside the grid's clip, over
 * the symbols.
 */
export class MultiplierBadgesView extends Container {
  private readonly multipliers = new Map<number, Multiplier>();
  private readonly cellPosition: (index: number) => { x: number; y: number };
  private readonly runner: PresentationTaskRunner;

  public constructor(input: { cellPosition: (index: number) => { x: number; y: number }; runner: PresentationTaskRunner }) {
    super({ label: "MultiplierBadges" });
    this.cellPosition = input.cellPosition;
    this.runner = input.runner;
  }

  /**
   * Cell `index` shows ×`multiplier`: popping in, or swelling when it raises a multiplier already there; at once when
   * `carried` (its number arrived on a fire streak's head, animated on its way: it shows as it arrived).
   */
  public async set(index: number, multiplier: number, popSeconds: number, carried = false): Promise<void> {
    let shown = this.multipliers.get(index);
    const swell = shown !== undefined;
    if (!shown) {
      const root = new Container({ label: `Multiplier${index}` });
      const at = this.spot(index);
      root.position.set(at.x, at.y);
      const text = multiplierLabel("");
      root.addChild(text);
      this.addChild(root);
      shown = { root, text };
      this.multipliers.set(index, shown);
    }
    setMultiplierText(shown.text, `x${multiplier}`);
    const root = shown.root;
    if (carried) {
      root.scale.set(1, 1);
      return;
    }
    await this.runner.tweenProgress(popSeconds, (k) => {
      if (root.destroyed) return;
      const s = swell ? 1 + 0.3 * Math.sin(Math.PI * k) : k < 0.5 ? 0.3 + 1.6 * k * 1.2 : 1 + 0.3 * Math.sin(Math.PI * k);
      root.scale.set(s, s);
    }, FX_SCOPE, { easing: "linear" });
    if (!root.destroyed) root.scale.set(1, 1);
  }

  /** Where cell `index`'s multiplier sits, in the grid root's space (a streak's number settles there). */
  public spot(index: number): { x: number; y: number } {
    const at = this.cellPosition(index);
    return { x: at.x + MULTIPLIER_LAYOUT.offset.x, y: at.y + MULTIPLIER_LAYOUT.offset.y };
  }

  /**
   * Cell `index`'s multiplier goes into what the cell holds as the board pays it: it moves from its corner onto the cell's
   * centre, growing (`moveSeconds`), swells there and fades out (`popSeconds`), and is gone. Nothing without one.
   */
  public async merge(index: number, moveSeconds: number, popSeconds: number): Promise<void> {
    const shown = this.multipliers.get(index);
    if (!shown) return;
    this.multipliers.delete(index);
    const { root } = shown;
    const from = { x: root.x, y: root.y };
    const to = this.cellPosition(index);
    try {
      await this.runner.tweenProgress(moveSeconds, (k) => {
        if (root.destroyed) return;
        root.position.set(from.x + (to.x - from.x) * k, from.y + (to.y - from.y) * k);
        root.scale.set(1 + 0.4 * k, 1 + 0.4 * k);
      }, FX_SCOPE, { easing: "quadInOut" });
      await this.runner.tweenProgress(popSeconds, (k) => {
        if (root.destroyed) return;
        const s = 1.4 + 0.3 * Math.sin(Math.PI * Math.min(1, k * 1.6));
        root.scale.set(s, s);
        root.alpha = k < 0.55 ? 1 : 1 - (k - 0.55) / 0.45;
      }, FX_SCOPE, { easing: "linear" });
    } finally {
      root.destroy({ children: true });
    }
  }

  /** Every multiplier on the board, by cell. */
  public values(): Map<number, number> {
    return new Map([...this.multipliers].map(([index, shown]) => [index, Number(shown.text.text.slice(1))]));
  }

  public clear(): void {
    for (const shown of this.multipliers.values()) shown.root.destroy({ children: true });
    this.multipliers.clear();
  }
}
